import { useState } from "react";

import {
    CheckCircle,
    ChefHat,
    PackageCheck,
    Bike,
    Truck,
    XCircle,
    RotateCcw,
    Printer,
    Download,
    Loader2,
    Trash2,
} from "lucide-react";

import {
    updateOrderStatus,
    updatePaymentStatus,
    deleteOrder,
} from "../../services/adminApi";

import "./OrderActions.css";

function OrderActions({
    order,
    refreshOrder,
}) {

    const [loading, setLoading] = useState(false);

    // =====================================
    // Update Order Status
    // =====================================

    const changeStatus = async (status) => {

        try {

            setLoading(true);

            await updateOrderStatus(
                order._id,
                status
            );

            // COD Payment Received
            if (
                status === "Delivered" &&
                order.paymentMethod ===
                    "Cash On Delivery"
            ) {

                await updatePaymentStatus(
                    order._id,
                    "Paid"
                );

            }

            await refreshOrder();

        }

        catch (error) {

            console.log(error);

            alert(
                error.response?.data?.message ||
                "Failed to update order."
            );

        }

        finally {

            setLoading(false);

        }

    };

    // =====================================
    // Cancel Order
    // =====================================

    const cancelOrder = async () => {

        const ok = window.confirm(
            "Cancel this order?"
        );

        if (!ok) return;

        await changeStatus("Cancelled");

    };

    // =====================================
    // Delete Order
    // =====================================

    const removeOrder = async () => {

        const ok = window.confirm(
            "Delete this order permanently?"
        );

        if (!ok) return;

        try {

            setLoading(true);

            await deleteOrder(order._id);

            alert(
                "Order deleted successfully."
            );

            window.history.back();

        }

        catch (error) {

            console.log(error);

        }

        finally {

            setLoading(false);

        }

    };

    // =====================================
    // Print Invoice
    // =====================================

    const printInvoice = () => {

        window.print();

    };

    // =====================================
    // Download Invoice
    // =====================================

    const downloadInvoice = () => {

        alert(
            "PDF Invoice Download Coming Soon"
        );

    };

    // =====================================
    // Refund
    // =====================================

    const refundPayment = async () => {

        if (
            order.paymentStatus !== "Paid"
        ) {

            return alert(
                "Payment is not completed."
            );

        }

        const ok = window.confirm(
            "Process refund?"
        );

        if (!ok) return;

        try {

            setLoading(true);

            await updatePaymentStatus(
                order._id,
                "Refunded"
            );

            await refreshOrder();

        }

        catch (error) {

            console.log(error);

        }

        finally {

            setLoading(false);

        }

    };
      // =====================================
    // Button Disabled Helpers
    // =====================================

    const isPending =
        order.status === "Pending";

    const isAccepted =
        order.status === "Accepted";

    const isPreparing =
        order.status === "Preparing";

    const isReady =
        order.status === "Ready For Pickup";

    const isAssigned =
        order.status === "Assigned";

    const isPickedUp =
        order.status === "Picked Up";

    const isOutForDelivery =
        order.status === "Out For Delivery";

    const isDelivered =
        order.status === "Delivered";

    const isCancelled =
        order.status === "Cancelled" ||
        order.status === "Rejected";

    return (

        <div className="order-actions">

            <div className="order-actions-header">

                <h2>Order Actions</h2>

                <span>

                    Current Status :

                    <strong>

                        {order.status}

                    </strong>

                </span>

            </div>

            <div className="actions-grid">

                {/* Accept */}

                <button

                    disabled={
                        loading ||
                        !isPending
                    }

                    className="success"

                    onClick={() =>
                        changeStatus("Accepted")
                    }

                >

                    {

                        loading ?

                        <Loader2
                            size={18}
                            className="spin"
                        />

                        :

                        <CheckCircle
                            size={18}
                        />

                    }

                    Accept

                </button>

                {/* Preparing */}

                <button

                    disabled={
                        loading ||
                        !isAccepted
                    }

                    className="primary"

                    onClick={() =>
                        changeStatus("Preparing")
                    }

                >

                    <ChefHat size={18} />

                    Preparing

                </button>

                {/* Ready */}

                <button

                    disabled={
                        loading ||
                        !isPreparing
                    }

                    className="info"

                    onClick={() =>
                        changeStatus(
                            "Ready For Pickup"
                        )
                    }

                >

                    <PackageCheck
                        size={18}
                    />

                    Ready

                </button>

                {/* Assigned */}

                <button

                    disabled={
                        loading ||
                        !isReady
                    }

                    className="warning"

                    onClick={() =>
                        changeStatus(
                            "Assigned"
                        )
                    }

                >

                    <Bike size={18} />

                    Assign Rider

                </button>

                {/* Picked Up */}

                <button

                    disabled={
                        loading ||
                        !isAssigned
                    }

                    className="primary"

                    onClick={() =>
                        changeStatus(
                            "Picked Up"
                        )
                    }

                >

                    <Truck size={18} />

                    Picked Up

                </button>

                {/* Out For Delivery */}

                <button

                    disabled={
                        loading ||
                        !isPickedUp
                    }

                    className="warning"

                    onClick={() =>
                        changeStatus(
                            "Out For Delivery"
                        )
                    }

                >

                    <Bike size={18} />

                    Out For Delivery

                </button>

                {/* Delivered */}

                <button

                    disabled={
                        loading ||
                        !isOutForDelivery
                    }

                    className="success"

                    onClick={() =>
                        changeStatus(
                            "Delivered"
                        )
                    }

                >

                    <CheckCircle
                        size={18}
                    />

                    Delivered

                </button>

                {/* Cancel */}

                <button

                    disabled={
                        loading ||
                        isDelivered ||
                        isCancelled
                    }

                    className="danger"

                    onClick={cancelOrder}

                >

                    <XCircle size={18} />

                    Cancel

                </button>

                {/* Refund */}

                <button

                    disabled={
                        loading ||
                        order.paymentStatus !==
                        "Paid"
                    }

                    className="secondary"

                    onClick={
                        refundPayment
                    }

                >

                    <RotateCcw
                        size={18}
                    />

                    Refund

                </button>

                {/* Print */}

                <button

                    className="dark"

                    onClick={
                        printInvoice
                    }

                >

                    <Printer size={18} />

                    Print Invoice

                </button>

                {/* Download */}

                <button

                    className="download"

                    onClick={
                        downloadInvoice
                    }

                >

                    <Download
                        size={18}
                    />

                    Download

                </button>

                {/* Delete */}

                <button

                    className="delete"

                    onClick={
                        removeOrder
                    }

                >

                    <Trash2 size={18} />

                    Delete Order

                </button>

            </div>

        </div>

    );

}

export default OrderActions;